<script lang="ts" setup>
/**
 * This component is currently used implicitly only in FI and SE
 * where currently signatures are only available addon
 */

const props = defineProps<{
  disabled?: boolean
}>()

const { t } = useI18n()
const { country } = useAuth()
const { formatCurrency } = useIntl()
const { priceList, paymentPeriodUnit, addons } = useSubscriptionProcessContext()

const signingFeatureConfig = computed(() => {
  if (!priceList.dataWithoutUpgrades.value?.data?.feature_configs)
    return undefined

  const config = priceList.dataWithoutUpgrades.value.data.feature_configs
    .find(({ feature_id }) => feature_id === 'signing')

  return config
})

const signingAddonIdsByConfig = computed(() => signingFeatureConfig.value?.options.flatMap(({ options }) => options.map(({ addon_id }) => addon_id)).filter(Boolean) ?? [])

const availableAddons = computed(() => priceList.dataWithoutUpgrades.value?.addons ?? [])

const selectedSignatureAddonId = computed(() => {
  const addonId = addons.value.find(({ addonId }) => signingAddonIdsByConfig.value.includes(addonId))?.addonId

  return availableAddons.value.find(addon => addon.addon_id === addonId)?.addon_id
})

const isOpen = ref(!!signingFeatureConfig.value && !!selectedSignatureAddonId.value)

watch(isOpen, (isOpen, oldOpenState) => {
  if (!isOpen && oldOpenState && selectedSignatureAddonId.value)
    addons.value = addons.value.filter(({ addonId }) => addonId !== selectedSignatureAddonId.value)
})

const signatureAddonOptions = computed(() => {
  if (!signingFeatureConfig.value)
    return undefined

  const mappedOptions = signingFeatureConfig.value.options.map(({ options }) => ([
    ...options.map((opt) => {
      const addon = availableAddons.value.find(({ addon_id }) => addon_id === opt.addon_id)
      if (!addon)
        return undefined

      return {
        ...opt,
        price: addon.prices.find(({ period_unit }) => period_unit === paymentPeriodUnit.value),
      }
    }).filter(Boolean),
  ]))

  return [...mappedOptions]
    .filter(opts => !!opts.length)
    .flatMap(opts => opts.map((opt) => {
      if (!opt?.addon_id || !opt.price || !opt.quantity)
        return undefined

      return {
        quantity: opt.quantity,
        addon_id: opt.addon_id,
        label: t('subscribe.signature-addon.option.label', {
          quantity: opt.quantity,
          price: formatCurrency(opt.price.monthly_price / 100),
        }),
        description: t('subscribe.signature-addon.option.description', {
          price: formatCurrency(
            ((paymentPeriodUnit.value === 'month'
              ? opt.price.price * 12
              : opt.price.price) / 100)
            / opt.quantity,
          ),
        }),
      }
    }))
    .filter(Boolean)
})

const handleGroupToggle = (value: boolean) => {
  if (props.disabled)
    return

  if (value) {
    addons.value.push({
      addonId: signatureAddonOptions.value?.[0]?.addon_id || '',
      quantity: 1,
    })
  }

  isOpen.value = value
}

const defaultSignaturePriceText = computed(() => {
  // TODO: DEV-6748 use dynamic signature price value here
  switch (country.value) {
    case 'FI':
      return formatCurrency(1.35)
    case 'SE':
      return formatCurrency(10)
    default:
      return undefined
  }
})

const handleSignatureAddonChange = (addonId?: string) => {
  if (selectedSignatureAddonId.value) {
    addons.value = addons.value.filter(({ addonId }) => addonId !== selectedSignatureAddonId.value)
  }

  if (addonId) {
    addons.value.push({ addonId, quantity: 1 })
  }
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <span class="text-xl font-semibold">{{ t('addons') }}</span>
    <ContentGroupToggleable
      :model-value="isOpen"
      :title="t('subscribe.signature-addon.title')"
      class="rounded-lg border border-gray-200 p-3.5"
      @update:model-value="handleGroupToggle"
    >
      <template #precontent>
        <p
          class="mr-10 text-pretty pb-2 text-sm text-gray-600"
        >
          {{ t('subscribe.signature-addon.info-text', { price: defaultSignaturePriceText }) }}
        </p>
      </template>
      <div
        v-if="selectedSignatureAddonId"
        class="flex flex-col gap-3"
      >
        <span class="text-sm font-medium">{{ t('subscribe.signature-addon.select-text') }}</span>
        <DTRadio
          v-for="option, index in signatureAddonOptions"
          :key="index"
          :model-value="selectedSignatureAddonId"
          :value="option?.addon_id"
          :label="option?.label ?? ''"
          :description="option?.description"
          :disabled="disabled"
          @update:model-value="handleSignatureAddonChange(option?.addon_id)"
        />
      </div>
    </ContentGroupToggleable>
  </div>
</template>
